import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { FaqCategoryKey, FaqItem } from '../types';

interface UseFaqItemsReturn {
  faqItems: FaqItem[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  addFaqItem: (categoryKey: FaqCategoryKey, question: string, answer: string) => Promise<boolean>;
  updateFaqItem: (id: string, categoryKey: FaqCategoryKey, question: string, answer: string) => Promise<boolean>;
  deleteFaqItem: (id: string) => Promise<boolean>;
  reorderFaqItems: (orderedIds: string[]) => Promise<boolean>;
}

export function useFaqItems(): UseFaqItemsReturn {
  const [faqItems, setFaqItems] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('faq_items')
        .select('id, category_key, question, answer, sort_order, created_at')
        .order('category_key')
        .order('sort_order');
      if (err) throw err;
      setFaqItems(data ?? []);
      setError(null);
    } catch {
      setError('Kon de veelgestelde vragen niet laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addFaqItem = async (categoryKey: FaqCategoryKey, question: string, answer: string): Promise<boolean> => {
    try {
      const nextSortOrder =
        Math.max(0, ...faqItems.filter((f) => f.category_key === categoryKey).map((f) => f.sort_order)) + 1;
      const { error: err } = await supabase
        .from('faq_items')
        .insert({ category_key: categoryKey, question, answer, sort_order: nextSortOrder });
      if (err) throw err;
      await load();
      return true;
    } catch {
      return false;
    }
  };

  const updateFaqItem = async (
    id: string,
    categoryKey: FaqCategoryKey,
    question: string,
    answer: string,
  ): Promise<boolean> => {
    try {
      const { error: err } = await supabase
        .from('faq_items')
        .update({ category_key: categoryKey, question, answer })
        .eq('id', id);
      if (err) throw err;
      setFaqItems((prev) =>
        prev.map((f) => (f.id === id ? { ...f, category_key: categoryKey, question, answer } : f)),
      );
      return true;
    } catch {
      return false;
    }
  };

  const deleteFaqItem = async (id: string): Promise<boolean> => {
    try {
      const { error: err } = await supabase.from('faq_items').delete().eq('id', id);
      if (err) throw err;
      setFaqItems((prev) => prev.filter((f) => f.id !== id));
      return true;
    } catch {
      return false;
    }
  };

  const reorderFaqItems = async (orderedIds: string[]): Promise<boolean> => {
    const snapshot = faqItems;
    const sortOrderById = Object.fromEntries(orderedIds.map((id, index) => [id, index + 1]));
    setFaqItems((prev) => prev.map((f) => (f.id in sortOrderById ? { ...f, sort_order: sortOrderById[f.id] } : f)));
    try {
      const results = await Promise.all(
        orderedIds.map((id, index) => supabase.from('faq_items').update({ sort_order: index + 1 }).eq('id', id)),
      );
      const firstError = results.find((r) => r.error)?.error;
      if (firstError) throw firstError;
      return true;
    } catch {
      setFaqItems(snapshot);
      return false;
    }
  };

  return {
    faqItems,
    loading,
    error,
    reload: load,
    addFaqItem,
    updateFaqItem,
    deleteFaqItem,
    reorderFaqItems,
  };
}
